import { action, makeAutoObservable, observable, reaction } from "mobx";
import { createContext } from "react";

import * as SportRequest from "../axios/routes/sport";
import {
  addGameRequest, changeGameStatusRequest, markOperatorGameLiveRequest, deleteGameRequest,
  getGameDataByIdRequest, getGamesPopulatedRequest, updateGameIsPaidGameRequest,
  updateGameRequest, updateGameTemplateRequest, updateGameShowPrintOrderButtonRequest,
  updateGameTemplatesRequest,
  updateGameCheckLastNameRequest,
  updateGameSecondaryLogoColorDuoRequest,
} from "../axios/routes/games";
import { TableTag } from "../interfaces/base";
import {
  GameInput,
  GetGameListRequest,
  NameInput,
  OperatorGamePopulated,
  TeamPopulated,
  UpdateGameRequest,
} from "../shared/interfaces";
import { DEFAULT_SELECT_VALUE } from "../utils/constants";
import { getTeamListRequest } from "../axios/routes/teams";
import { GameStatus } from "../interfaces/game";
import { NotificationServiceInstance } from "./notification";
import { SessionServiceInstance } from "./session";
import { isOperatorDashboardLivePhase } from "../utils/gameEffectiveStart";

function isValidOperatorGameRow(game: OperatorGamePopulated | null | undefined): boolean {
  return !!(
    game?._id
    && game?.team1?._id
    && game?.team1?.name
    && game?.team2?._id
    && game?.team2?.name
    && game?.stadium?._id
    && game?.sport?._id
    && game?.league?._id
    && Number.isFinite(Number(game.startDate))
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function getGamesPopulatedWithRetry(
  query: Parameters<typeof getGamesPopulatedRequest>[0],
  maxAttempts = 3,
): Promise<OperatorGamePopulated[]> {
  const attemptFetch = async (attemptIndex: number): Promise<OperatorGamePopulated[]> => {
    try {
      return await getGamesPopulatedRequest(query);
    } catch (err) {
      const error = err as Error;
      console.warn(
        `[GameService] fetchGames attempt ${attemptIndex + 1} failed:`,
        error.message,
      );
      if (attemptIndex >= maxAttempts - 1) {
        throw error;
      }
      await sleep(1000 * (attemptIndex + 1));
      return attemptFetch(attemptIndex + 1);
    }
  };

  return attemptFetch(0);
}

export interface GamePopulatedWithTag extends OperatorGamePopulated, TableTag {
  status: GameStatus,
}

class GameService {
  @observable games: GamePopulatedWithTag[] = [];

  @observable gamesLoading = false;

  @observable gamesReady = false;

  // Dropdown data
  @observable leagueList: NameInput[] = [];

  @observable cityList: NameInput[] = [];

  @observable stadiumList: NameInput[] = [];

  @observable teamList: TeamPopulated[] = [];

  // Filter values
  @observable selectedDate: Date = null;

  @observable allTime = true;

  @observable selectedUnixDate: number = null;

  @observable selectedSport: string = null;

  @observable selectedLeague: string = null;

  @observable selectedState: string = null;

  @observable selectedCity: string = null;

  @observable selectedStadium: string = null;

  @observable selectedTeam: string = null;

  /** True when the last all-time page was full — more older games may exist. */
  @observable gamesHasMore = false;

  @observable gamesLoadingMore = false;

  /** Page size for Games dashboard all-time fetch / Load more. */
  private static readonly GAMES_PAGE_SIZE = 200;

  constructor() {
    makeAutoObservable(this);

    const date = new Date();

    this.selectedDate = date;
    this.selectedUnixDate = date.setHours(0, 0, 0, 0);

    reaction(
      () => SessionServiceInstance.EmailAddress,
      (email) => {
        if (email) {
          this.fetchGames();
        }
      },
    );
  }

  private buildGamesListQuery = (offset: number): GetGameListRequest => {
    const { isSuperAdmin, EmailAddress } = SessionServiceInstance;
    const query: GetGameListRequest = {
      city: this.selectedCity || undefined,
      league: this.selectedLeague || undefined,
      sport: this.selectedSport || undefined,
      stadium: this.selectedStadium || undefined,
      state: this.selectedState || undefined,
      team: this.selectedTeam || undefined,
      date: this.allTime ? undefined : this.selectedUnixDate,
    };
    if (!isSuperAdmin && EmailAddress) {
      query.operatorEmail = EmailAddress;
    }
    // Paginate all-time lists so operators can Load more past the server cap.
    if (this.allTime) {
      query.limit = GameService.GAMES_PAGE_SIZE;
      query.offset = offset;
    }
    return query;
  };

  private mapGameRows = (rows: OperatorGamePopulated[]): GamePopulatedWithTag[] => (
    rows
      .filter(isValidOperatorGameRow)
      .map<GamePopulatedWithTag>((game) => ({
        ...game,
        status: this.calculateGameStatus(game),
      }))
      .sort((a, b) => b.startDate - a.startDate)
  );

  fetchGames = async () => {
    this.gamesLoading = true;
    this.gamesHasMore = false;
    const query = this.buildGamesListQuery(0);
    console.log(
      "[GameService] fetchGames",
      {
        isSuperAdmin: SessionServiceInstance.isSuperAdmin,
        EmailAddress: SessionServiceInstance.EmailAddress,
        operatorEmail: query.operatorEmail,
        limit: query.limit,
        offset: query.offset,
      },
    );

    try {
      const response = await getGamesPopulatedWithRetry(query);
      const rows = Array.isArray(response) ? response : [];
      const skipped = rows.length - rows.filter(isValidOperatorGameRow).length;
      if (skipped > 0) {
        console.warn(`[GameService] skipped ${skipped} invalid game row(s) from API response`);
      }
      console.log(
        "[GameService] fetchGames result:",
        rows.length,
        "games",
      );

      this.games = this.mapGameRows(rows);
      this.gamesHasMore = this.allTime && rows.length >= GameService.GAMES_PAGE_SIZE;
    } catch (err) {
      NotificationServiceInstance.showErrorNotification((err as Error).message);
      this.gamesHasMore = false;
    }

    this.gamesLoading = false;
    this.gamesReady = true;
  }

  /** Append the next page of older all-time games (newest-first offset). */
  @action public loadMoreGames = async (): Promise<void> => {
    if (!this.allTime || !this.gamesHasMore || this.gamesLoadingMore || this.gamesLoading) {
      return;
    }
    this.gamesLoadingMore = true;
    const offset = this.games.length;
    const query = this.buildGamesListQuery(offset);
    console.log("[GameService] loadMoreGames", { offset, limit: query.limit });

    try {
      const response = await getGamesPopulatedWithRetry(query);
      const rows = Array.isArray(response) ? response : [];
      const mapped = this.mapGameRows(rows);
      const existingIds = new Set(this.games.map((g) => g._id));
      const appended = mapped.filter((g) => !existingIds.has(g._id));
      this.games = [...this.games, ...appended].sort((a, b) => b.startDate - a.startDate);
      this.gamesHasMore = rows.length >= GameService.GAMES_PAGE_SIZE;
      console.log(
        "[GameService] loadMoreGames result:",
        rows.length,
        "raw,",
        appended.length,
        "new; hasMore=",
        this.gamesHasMore,
      );
    } catch (err) {
      NotificationServiceInstance.showErrorNotification((err as Error).message);
    }

    this.gamesLoadingMore = false;
  }

  @action public updateDraftMoments = (gameId: string): GamePopulatedWithTag|null => {
    const targetGame = this.games.find((g) => g._id === gameId);
    if (!targetGame) {
      console.log("[new draft moment][game service] moment's game is not fetched");
      return null;
    }

    targetGame.draftMoments = targetGame.draftMoments ? targetGame.draftMoments + 1 : 1;

    console.log(`[new draft moment][game service] draft moments counter was updated. Current draft moments: ${targetGame.draftMoments}`);
    return targetGame;
  }

  @action public toggleDate = () => {
    this.allTime = !this.allTime;
    this.fetchGames();
  }

  @action public onDateChange = (date: Date) => {
    this.selectedDate = date;
    this.selectedUnixDate = date.setHours(0, 0, 0, 0);
    this.fetchGames();
  }

  @action public onSportChange = (_id: string) => {
    this.clearLeague();
    this.clearTeam();

    if (_id === DEFAULT_SELECT_VALUE) {
      this.selectedSport = null;
    } else {
      this.selectedSport = _id;
      this.fetchLeagueList();
    }
    this.fetchGames();
  }

  @action public onLeagueChange = (_id: string) => {
    this.clearTeam();

    if (_id === DEFAULT_SELECT_VALUE) {
      this.selectedLeague = null;
    } else {
      this.selectedLeague = _id;
      this.fetchTeamList();
    }
    this.fetchGames();
  }

  @action private clearLeague = () => {
    this.leagueList = [];
    this.selectedLeague = null;
  }

  @action public onTeamChange = (_id: string) => {
    if (_id === DEFAULT_SELECT_VALUE) {
      this.selectedTeam = null;
    } else {
      this.selectedTeam = _id;
    }
    this.fetchGames();
  }

  @action private clearTeam = () => {
    this.teamList = [];
    this.selectedTeam = null;
  }

  @action public onStateChange = (_id: string) => {
    this.clearCity();
    this.clearStadium();

    if (_id === DEFAULT_SELECT_VALUE) {
      this.selectedState = null;
    } else {
      this.selectedState = _id;
      this.fetchCityList();
    }
    this.fetchGames();
  }

  @action public onCityChange = (_id: string) => {
    this.clearStadium();

    if (_id === DEFAULT_SELECT_VALUE) {
      this.selectedCity = null;
    } else {
      this.selectedCity = _id;
      this.fetchStadiumList();
    }
    this.fetchGames();
  }

  @action private clearCity = () => {
    this.cityList = [];
    this.selectedCity = null;
  }

  @action public onStadiumChange = (_id: string) => {
    if (_id === DEFAULT_SELECT_VALUE) {
      this.selectedStadium = null;
    } else {
      this.selectedStadium = _id;
    }
    this.fetchGames();
  }

  @action private clearStadium = () => {
    this.selectedStadium = null;
    this.stadiumList = [];
  }

  @action public onReloadGames = () => {
    this.fetchGames();
  }

  private fetchLeagueList = async () => {
    try {
      this.leagueList = await SportRequest.getLeagueListRequest({ sport: this.selectedSport });
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchSportList()", err.message);
    }
  }

  private fetchTeamList = async () => {
    try {
      let list = await getTeamListRequest({
        league: this.selectedLeague, sport: this.selectedSport,
      });
      const { isSuperAdmin, accessibleTeamIds } = SessionServiceInstance;
      if (!isSuperAdmin && accessibleTeamIds.length > 0) {
        list = list.filter((t) => accessibleTeamIds.includes(t._id));
      }
      this.teamList = list;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification(
        "fetchTeamList()", err.message,
      );
    }
  }

  private fetchCityList = async () => {
    try {
      this.cityList = await SportRequest.getCityListRequest({ state: this.selectedState });
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchStadiumList()", err.message);
    }
  }

  private fetchStadiumList = async () => {
    try {
      this.stadiumList = await SportRequest.getStadiumListRequest({
        city: this.selectedCity,
      });
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchStadiumList()", err.message);
    }
  }

  /** New array reference so React useMemo deps on `games` invalidate after updates. */
  private replaceGameAtIndex(index: number, game: GamePopulatedWithTag): void {
    if (index < 0) return;
    this.games = [
      ...this.games.slice(0, index),
      game,
      ...this.games.slice(index + 1),
    ];
  }

  /**
   * Applies a shallow merge to one cached games row without refetching the full list.
   * Do not assign to `games[i]` in place — the Games page memoizes filtered lists keyed on `games`
   * reference; this method replaces the observable array so that UI updates immediately.
   */
  mergeGameListRowById = (gameId: string, patch: Partial<GamePopulatedWithTag>): boolean => {
    const index = this.games.findIndex((g) => g._id === gameId);
    if (index < 0) return false;
    const current = this.games[index];
    this.replaceGameAtIndex(index, {
      ...current,
      ...patch,
    });
    return true;
  };

  private removeGameAtIndex(index: number): void {
    if (index < 0) return;
    this.games = [...this.games.slice(0, index), ...this.games.slice(index + 1)];
  }

  public fetchGameById = async (gameId: string): Promise<OperatorGamePopulated|null> => {
    try {
      return await getGameDataByIdRequest(gameId);
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("fetchGameById()", err.message);
      return null;
    }
  }

  public createGame = async (request: GameInput): Promise<boolean> => {
    try {
      const { _id } = await addGameRequest(request);
      if (!_id) return false;

      const newGame = await getGameDataByIdRequest(_id);
      const row: GamePopulatedWithTag = {
        ...newGame,
        tag: "new",
        status: this.calculateGameStatus(newGame),
      };
      this.games = [row, ...this.games];

      return true;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("createGame()", err.message);
      return false;
    }
  }

  public updateGame = async (request: UpdateGameRequest, _id: string): Promise<boolean> => {
    try {
      const email = SessionServiceInstance.EmailAddress;
      if (await updateGameRequest(request, _id, email)) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGame()", err.message);
      return false;
    }
  }

  public changeGameStatus = async (
    finishDate: number|null,
    archived: boolean,
    _id: string,
  ): Promise<boolean> => {
    try {
      if (await changeGameStatusRequest({ finishDate, archived }, _id)) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("changeGameStatus()", err.message);
      return false;
    }
  }

  public markOperatorGameLive = async (_id: string): Promise<boolean> => {
    try {
      if (await markOperatorGameLiveRequest(_id)) {
        const updatedGame = await getGameDataByIdRequest(_id);
        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification(
        "markOperatorGameLive()",
        err.message,
      );
      return false;
    }
  }

  public deleteGame = async (_id: string): Promise<boolean> => {
    try {
      if (await deleteGameRequest(_id)) {
        const index = this.games.findIndex((g) => g._id === _id);
        this.removeGameAtIndex(index);
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("deleteGame()", err.message);
      return false;
    }
  }

  private calculateGameStatus = (game: OperatorGamePopulated): GameStatus => {
    let status: GameStatus;

    if (game.archived) {
      status = "archived";
    } else if (game.finishDate) {
      status = "finished";
    } else {
      status = isOperatorDashboardLivePhase(game) ? "ongoing" : "upcoming";
    }

    return status;
  }

  public updateGameTemplate = async (
    ticketTemplate: string,
    _id: string,
  ): Promise<boolean> => {
    try {
      if (await updateGameTemplateRequest(ticketTemplate, _id)) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          // templates are expanded for getGameDataByIdRequest
          ticketTemplate: updatedGame.template?._id || "",
          fan1stTicketTemplate: updatedGame.fan1stTemplate?._id || "",
          specialSectionTicketTemplate: updatedGame.specialSectionTemplate?._id || "",
          specialTicketTemplate1: updatedGame.specialTemplate1?._id || "",
          specialTicketTemplate2: updatedGame.specialTemplate2?._id || "",
          specialTicketTemplate3: updatedGame.specialTemplate3?._id || "",
          specialTicketTemplate4: updatedGame.specialTemplate4?._id || "",
          specialTicketTemplate5: updatedGame.specialTemplate5?._id || "",
          specialTicketTemplate6: updatedGame.specialTemplate6?._id || "",
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGameTemplate()", err.message);
      return false;
    }
  }

  public updateGameTemplates = async (
    ticketTemplate: string,
    fan1stTicketTemplate: string,
    specialSectionTemplate: string,
    specialTemplate1: string,
    specialTemplate2: string,
    specialTemplate3: string,
    specialTemplate4: string,
    specialTemplate5: string,
    specialTemplate6: string,
    _id: string,
  ): Promise<boolean> => {
    try {
      if (
        await updateGameTemplatesRequest(
          ticketTemplate,
          fan1stTicketTemplate,
          specialSectionTemplate,
          specialTemplate1,
          specialTemplate2,
          specialTemplate3,
          specialTemplate4,
          specialTemplate5,
          specialTemplate6,
          _id,
        )
      ) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          // templates are expanded for getGameDataByIdRequest
          ticketTemplate: updatedGame.template?._id || "",
          fan1stTicketTemplate: updatedGame.fan1stTemplate?._id || "",
          specialSectionTicketTemplate: updatedGame.specialSectionTemplate?._id || "",
          specialTicketTemplate1: updatedGame.specialTemplate1?._id || "",
          specialTicketTemplate2: updatedGame.specialTemplate2?._id || "",
          specialTicketTemplate3: updatedGame.specialTemplate3?._id || "",
          specialTicketTemplate4: updatedGame.specialTemplate4?._id || "",
          specialTicketTemplate5: updatedGame.specialTemplate5?._id || "",
          specialTicketTemplate6: updatedGame.specialTemplate6?._id || "",
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGameTemplates()", err.message);
      return false;
    }
  }

  public updateGameSecondaryLogoColorDuo = async (
    _id: string,
    teamSecondaryLogoColorDuoId?: string | null,
    clearSelection?: boolean,
  ): Promise<boolean> => {
    try {
      if (await updateGameSecondaryLogoColorDuoRequest(
        _id,
        teamSecondaryLogoColorDuoId,
        clearSelection,
      )) {
        const updatedGame = await getGameDataByIdRequest(_id);
        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGameSecondaryLogoColorDuo()", err.message);
      return false;
    }
  };

  public updateGameIsPaidGame = async (
    isPaid: boolean,
    _id: string,
  ): Promise<boolean> => {
    try {
      if (await updateGameIsPaidGameRequest(isPaid, _id)) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          paidGame: !!updatedGame.paidGame,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGameIsPaidGame()", err.message);
      return false;
    }
  }

  public updateGameShowPrintOrderButton = async (
    showPrintOrderButton: boolean,
    _id: string,
  ): Promise<boolean> => {
    try {
      if (await updateGameShowPrintOrderButtonRequest(showPrintOrderButton, _id)) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          showPrintOrderButton: !!updatedGame.showPrintOrderButton,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGameShowPrintOrderButton()", err.message);
      return false;
    }
  }

  public updateGameCheckLastName = async (
    checkLastName: boolean,
    _id: string,
  ): Promise<boolean> => {
    try {
      if (await updateGameCheckLastNameRequest(checkLastName, _id)) {
        const updatedGame = await getGameDataByIdRequest(_id);

        const index = this.games.findIndex((g) => g._id === _id);
        this.replaceGameAtIndex(index, {
          ...updatedGame,
          checkLastName: !!updatedGame.checkLastName,
          tag: "updated",
          status: this.calculateGameStatus(updatedGame),
        });
        return true;
      }
      return false;
    } catch (err) {
      NotificationServiceInstance.showUnexpectedErrorNotification("updateGameCheckLastName()", err.message);
      return false;
    }
  }
}

export const GameServiceInstance = new GameService();

export const GameServiceContext = createContext(GameServiceInstance);
